import type { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import type { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { I18nService } from '@core/i18n-legacy';
import type { Hotspot, IntegrationKinds, Partner } from '@core/integrations-types';
import { camelCase } from '@core/utils-legacy';

import {
  ContextualIntegrationsApiService,
  CurrentHotspotToken,
  IntegrationKindToken,
  PartnerToken,
} from '../../../client/integrations-client.module';
import { HttpErrorHandlerService, LocalStorageService } from '../../../common';
import type { DynamicFormConfig } from '../../types';

export type Resource = { label: string; value: any };
export type ResourceError = { error: string };
export type Resources = { [type: string]: Resource[] };

@Injectable()
export class DynamicFormResourcesService {
  private readonly api = inject(ContextualIntegrationsApiService);
  private readonly httpErrorHandlerService = inject(HttpErrorHandlerService);
  private readonly localStorage = inject(LocalStorageService);
  private readonly i18nService = inject(I18nService);
  private readonly currentHotspot = inject<Hotspot>(CurrentHotspotToken);
  private readonly integrationKind = inject<IntegrationKinds>(IntegrationKindToken);
  private readonly partner = inject<Partner>(PartnerToken);

  public loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public formError$: BehaviorSubject<string[]> = new BehaviorSubject([]);

  public resources$: BehaviorSubject<Resources> = new BehaviorSubject(null!);
  public formConfig$: BehaviorSubject<DynamicFormConfig> = new BehaviorSubject(null!);

  constructor() {
    this.primeFromCache();
  }

  public get(type: string): Observable<Resource[] | ResourceError> {
    const camelizedType = camelCase(type);

    return this.resources$.pipe(
      filter(resources => !!resources),
      map(resources => (resources[camelizedType] ? resources[camelizedType] : [])),
    );
  }

  public fetchForm(formValue: object): void {
    this.loading$.next(true);

    this.api.post('resources', { form: formValue, hotspot: this.currentHotspot }).subscribe(
      (success: { form: DynamicFormConfig }) => {
        if (success.form) {
          this.formConfig$.next(success.form);
        }

        this.loading$.next(false);
      },
      (errorResponse: HttpErrorResponse) => {
        this.loading$.next(false);
        this.formError$.next(this.handleFormErrors(errorResponse));
      },
    );
  }

  public fetchResources(formValue: object): void {
    this.loading$.next(true);

    this.api.post('resources', { form: formValue, hotspot: this.currentHotspot }).subscribe(
      (success: { resources?: Resources; form?: DynamicFormConfig }) => {
        if (success.form) {
          this.formConfig$.next(success.form);
        }
        if (success.resources) {
          this.resources$.next(success.resources);
          this.localStorage.set(this.cacheKey(), success.resources);
        }

        this.loading$.next(false);
      },
      error => {
        this.loading$.next(false);
        this.httpErrorHandlerService.default(error);
      },
    );
  }

  private handleFormErrors(errorResponse: HttpErrorResponse): string[] {
    if (errorResponse.error && errorResponse.error.error && errorResponse.error.error.length) {
      return errorResponse.error.error;
    } else {
      return [this.i18nService.translate('rti.common.request_error')];
    }
  }

  private primeFromCache(): void {
    const cached = this.localStorage.get(this.cacheKey());
    if (cached) this.resources$.next(cached);
  }

  private cacheKey(): string {
    if (this.integrationKind) {
      return `dynamic-form-resources-${this.integrationKind}`;
    } else if (this.partner) {
      return `dynamic-form-resources-${this.partner.codename}`;
    } else {
      throw new Error(`no kind`);
    }
  }
}
